import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { WebSocketService } from '@qtek/core/websockets-core';
import { HTTP_BASE_URL, NetworkFailureKind } from '@qtek/shared/models';
import { OnlineService } from '@qtek/shared/services';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  firstValueFrom,
  interval,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  retry,
  shareReplay,
  skip,
  Subject,
  Subscription,
  switchMap,
  take,
  tap,
  timeout,
  timer,
} from 'rxjs';

const DEFAULT_ENDPOINT = '/actuator/health/serverStatusService';
const PROBE_TIMEOUT_MS = 5000;
// A burst of failing requests must cost one probe, not one each.
const PROBE_REUSE_MS = 3000;

@Injectable({
  providedIn: 'root',
})
export class HealthcheckCoreService {
  private httpBaseUrl = inject(HTTP_BASE_URL);
  private webSocketService = inject(WebSocketService);
  private onlineService = inject(OnlineService);
  private httpClient = inject(HttpClient);
  // Straight to the backend handler: the probe must not re-enter the error
  // interceptor (which would report/probe its own failure).
  private probeHttp = new HttpClient(inject(HttpBackend));
  private endpoint = DEFAULT_ENDPOINT;
  private probe$: Observable<boolean> | null = null;
  private lastProbe: { at: number; result: boolean } | null = null;

  private readonly isHealthyHttpConnectionSubject = new BehaviorSubject(true);
  public readonly isHealthyHttpConnection$ = this.isHealthyHttpConnectionSubject
    .asObservable()
    .pipe(distinctUntilChanged());
  public readonly isWebSocketAlive$ =
    this.webSocketService.getWebSocketIsAlive$();
  private isHealthyHttpConnectionSubscription: Subscription | null = null;
  private retryDelay = (interval: number) => {
    this.isHealthyHttpConnectionSubject.next(false);
    return timer(interval);
  };
  readonly isHealthy$ = combineLatest([
    this.isHealthyHttpConnection$,
    this.isWebSocketAlive$,
  ]).pipe(
    map(
      ([isHealthyHttpConnection, isAlive]) => isHealthyHttpConnection && isAlive
    ),
    distinctUntilChanged(),
    debounceTime(250)
  );

  private checkHttp$ = new Subject<void>();

  registerCheckHealthSubscription(
    interval$: Observable<number>,
    endpoint$: Observable<string>
  ): void {
    this.onlineService
      .change()
      .pipe(
        distinctUntilChanged(),
        filter(Boolean),
        skip(1),
        tap(() => {
          this.webSocketService.reconnect();
          this.checkHttp$.next();
        })
      )
      .subscribe();

    endpoint$.pipe(take(1)).subscribe(endpoint => (this.endpoint = endpoint));

    this.isHealthyHttpConnectionSubscription?.unsubscribe();
    this.isHealthyHttpConnectionSubscription = combineLatest([
      interval$,
      endpoint$,
    ])
      .pipe(
        take(1),
        mergeMap(([intervalValue, endpoint]) => {
          return merge(interval(intervalValue * 1000), this.checkHttp$).pipe(
            switchMap(() =>
              this.httpClient.head(`${this.httpBaseUrl}${endpoint}`)
            ),
            retry({
              resetOnSuccess: true,
              delay: () => this.retryDelay(intervalValue),
            })
          );
        }),
        catchError(error => {
          this.isHealthyHttpConnectionSubject.next(false);
          console.error(
            'There is a problem with connection to Backend please try refresh app, if problems persist please contact your support',
            error
          );
          return EMPTY;
        }),
        tap(() => {
          this.isHealthyHttpConnectionSubject.next(true);
        })
      )
      .subscribe();
  }

  public setHttpUnhealthy() {
    this.isHealthyHttpConnectionSubject.next(false);
  }

  /**
   * On-demand backend reachability probe. Any answer below 500 proves the
   * backend is up; a network error, timeout or 5xx means it is not. Updates
   * the shared health state. Concurrent callers share one request and a
   * fresh result is reused briefly.
   */
  verifyHttp(): Observable<boolean> {
    if (this.lastProbe && Date.now() - this.lastProbe.at < PROBE_REUSE_MS) {
      return of(this.lastProbe.result);
    }

    this.probe$ ??= this.probeHttp
      .head(`${this.httpBaseUrl}${this.endpoint}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store',
          'ngsw-bypass': 'true',
        },
      })
      .pipe(
        timeout(PROBE_TIMEOUT_MS),
        map(() => true),
        catchError(error =>
          of(
            error instanceof HttpErrorResponse &&
              error.status > 0 &&
              error.status < 500
          )
        ),
        tap(result => {
          this.lastProbe = { at: Date.now(), result };
          this.isHealthyHttpConnectionSubject.next(result);
        }),
        finalize(() => (this.probe$ = null)),
        shareReplay(1)
      );

    return this.probe$;
  }

  /**
   * Tells a blocked single request from a dead backend: the browser being
   * offline, else a probe — backend answers → only this request was killed.
   */
  async classifyNetworkFailure(): Promise<NetworkFailureKind> {
    if (!this.onlineService.isOnline()) {
      return 'offline';
    }

    return (await firstValueFrom(this.verifyHttp()))
      ? 'endpoint_blocked'
      : 'backend_unreachable';
  }
}
